import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, descargar, subirArchivo } from "../lib/api";
import { creditos, fecha, fechaHora, formatearCantidad, gramos } from "../lib/formato";
import {
  Aviso,
  BarraLimite,
  Cabecera,
  Campo,
  Cargando,
  EstadoSocio,
  FotoSocio,
  Modal,
  Panel,
  Vacio,
} from "../componentes/piezas";
import { useSesion } from "../lib/sesion";
import DialogoCobro from "../componentes/DialogoCobro";
import ContratoDelSocio from "../componentes/ContratoDelSocio";
import DialogoRecarga from "../componentes/DialogoRecarga";
import { IDIOMAS, useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

interface Socio {
  id: string;
  numeroSocio: number;
  nombre: string;
  apellidos: string;
  documentoTipo: string;
  documentoNumero: string;
  fechaNacimiento: string;
  telefono: string | null;
  email: string | null;
  direccion: string | null;
  ciudad: string | null;
  cp: string | null;
  estado: string;
  fechaAlta: string;
  fechaCaducidad: string | null;
  saldoCentimos: number;
  consumoPrevistoMensualMg: number;
  notas: string | null;
  idioma: string | null;
  consentimientoRgpd: boolean;
  fotoUrl: string | null;
  docFrenteUrl: string | null;
  docReversoUrl: string | null;
  portalActivo: boolean;
  avalista: { id: string; numeroSocio: number; nombre: string; apellidos: string } | null;
  avalados: { id: string; numeroSocio: number; nombre: string; apellidos: string }[];
  tipoSocio: { id: string; nombre: string; color: string; cuotaAnualCentimos: number | null; descuentoPorcentaje: number } | null;
}

interface Ficha {
  socio: Socio;
  consumo: {
    limiteDiarioMg: number;
    limiteMensualMg: number;
    consumidoHoyMg: number;
    consumidoMesMg: number;
    disponibleMg: number;
  };
  bloqueo: string | null;
  edad: number;
}

interface Historial {
  dispensaciones: {
    id: string;
    numero: number;
    fecha: string;
    totalMg: number;
    totalCentimos: number;
    estado: string;
    metodoPago: string;
    lineas: { nombreProducto: string; cantidadMg: number; unidad: string }[];
  }[];
  cuotas: { id: string; fecha: string; importeCentimos: number; periodoFin: string; concepto: string; metodoPago: string }[];
  cartera: { id: string; fecha: string; tipo: string; importeCentimos: number; saldoResultante: number; concepto: string | null }[];
  accesos: { id: string; fecha: string; tipo: string }[];
}

type Pestana = "resumen" | "dispensaciones" | "cartera" | "cuotas";

export default function SocioFicha() {
  const { t, cuenta, delServidor } = useIdioma();
  const { id = "" } = useParams();
  const navegar = useNavigate();
  const clienteConsultas = useQueryClient();
  const { puede, club } = useSesion();
  const [pestana, setPestana] = useState<Pestana>("resumen");
  const [dialogo, setDialogo] = useState<
    "recarga" | "cuota" | "devolver" | "ajustar" | "baja" | "suspender" | "borrar" | null
  >(null);
  const [error, setError] = useState("");
  const [codigoPortal, setCodigoPortal] = useState<{ codigo: string; numeroSocio: number } | null>(null);

  const ficha = useQuery({ queryKey: ["socio", id], queryFn: () => api<Ficha>(`/socios/${id}`) });
  const historial = useQuery({ queryKey: ["socio-historial", id], queryFn: () => api<Historial>(`/socios/${id}/historial`) });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ["socio", id] });
    void clienteConsultas.invalidateQueries({ queryKey: ["socio-historial", id] });
  };

  const subirFoto = useMutation({
    mutationFn: (archivo: File) => subirArchivo(`/socios/${id}/foto`, archivo),
    onSuccess: refrescar,
  });

  if (ficha.isLoading || !ficha.data) return <Cargando />;
  const { socio, consumo, bloqueo, edad } = ficha.data;

  return (
    <>
      <Cabecera
        titulo={`${socio.nombre} ${socio.apellidos}`}
        descripcion={t("se.descripcion", { numero: socio.numeroSocio })}
      >
        <Link className="boton secundario" to="/socios">
          {t("sf.volver")}
        </Link>
        <Link className="boton secundario" to={`/socios/carnets?ids=${socio.id}`}>
          {t("sf.carnet")}
        </Link>
        {puede("editarSocio") && (
          <Link className="boton secundario" to={`/socios/${socio.id}/editar`}>
            {t("sf.editar")}
          </Link>
        )}
        {puede("dispensar") && socio.estado === "ACTIVO" && (
          <Link className="boton" to={`/mostrador/${socio.id}`}>
            {t("sf.dispensar")}
          </Link>
        )}
      </Cabecera>

      {/* El motivo por el que no puede dispensar lo manda la API en castellano. */}
      {bloqueo && <Aviso tipo="atencion">{delServidor(bloqueo)}</Aviso>}
      <Aviso tipo="error">{error}</Aviso>

      <div className="rejilla ficha-socio">
        {/* --- Columna izquierda: identidad y acciones --- */}
        <div className="columna" style={{ gap: 16 }}>
          <Panel>
            <div className="fila" style={{ alignItems: "flex-start", gap: 14 }}>
              <FotoSocio socio={socio} grande />
              <div style={{ minWidth: 0, flex: 1 }}>
                <h2 style={{ lineHeight: 1.2 }}>
                  {socio.nombre} {socio.apellidos}
                </h2>
                <div className="fila" style={{ marginTop: 6 }}>
                  <EstadoSocio estado={socio.estado} />
                  {socio.tipoSocio && (
                    <span className="etiqueta" style={{ background: `${socio.tipoSocio.color}20`, color: socio.tipoSocio.color }}>
                      {socio.tipoSocio.nombre}
                      {socio.tipoSocio.descuentoPorcentaje > 0 ? ` −${socio.tipoSocio.descuentoPorcentaje}%` : ""}
                    </span>
                  )}
                  <span className="suave pequeno">{cuenta(edad, "sf.unAnio", "sf.anios")}</span>
                </div>
              </div>
            </div>

            <label className="boton secundario bloque" style={{ marginTop: 14, cursor: "pointer" }}>
              {subirFoto.isPending
                ? t("sf.subiendo")
                : socio.fotoUrl
                  ? t("sf.cambiarFoto")
                  : t("sf.anadirFoto")}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={(e) => {
                  const archivo = e.target.files?.[0];
                  if (archivo) subirFoto.mutate(archivo);
                }}
              />
            </label>
            {socio.fotoUrl && puede("editarSocio") && (
              <button
                className="boton fantasma bloque"
                onClick={() =>
                  api(`/socios/${id}/foto`, { metodo: "DELETE" })
                    .then(refrescar)
                    .catch((fallo) => setError(fallo instanceof Error ? fallo.message : t("sf.falloQuitarFoto")))
                }
              >
                {t("sf.quitarFoto")}
              </button>
            )}

            <dl style={{ margin: "16px 0 0", display: "grid", gap: 9 }}>
              <Dato etiqueta={t("sf.documento")} valor={`${socio.documentoTipo} ${socio.documentoNumero}`} />
              <Dato etiqueta={t("sn.telefono")} valor={socio.telefono ?? "—"} />
              <Dato etiqueta={t("sn.email")} valor={socio.email ?? "—"} />
              <Dato etiqueta={t("sf.alta")} valor={fecha(socio.fechaAlta)} />
              <Dato etiqueta={t("sf.cuotaHasta")} valor={fecha(socio.fechaCaducidad)} />
              {/* En el que le llegan los correos. Se ve aqui para no tener que
                  entrar a modificar la ficha solo para comprobarlo. */}
              <Dato
                etiqueta={t("sn.idioma")}
                valor={
                  IDIOMAS.find((i) => i.codigo === socio.idioma)?.nombre ??
                  t("sn.idiomaDelClub")
                }
              />
              <Dato
                etiqueta={t("sf.avalista")}
                valor={
                  socio.avalista ? (
                    <Link to={`/socios/${socio.avalista.id}`}>
                      #{socio.avalista.numeroSocio} {socio.avalista.nombre}
                    </Link>
                  ) : (
                    "—"
                  )
                }
              />
              <Dato
                etiqueta={t("sf.rgpd")}
                valor={socio.consentimientoRgpd ? t("sn.r.firmado") : t("sn.r.pendiente")}
              />
              <Dato
                etiqueta={t("sf.portal")}
                valor={socio.portalActivo ? t("sf.activo") : t("sf.sinAcceso")}
              />
            </dl>
          </Panel>

          <FotosDelDocumento socio={socio} alCambiar={refrescar} />

          <ContratoDelSocio socioId={socio.id} />

          <Panel titulo={t("sf.cartera")}>
            <div className="valor tabular" style={{ fontSize: "1.9rem", fontWeight: 680 }}>
              {creditos(socio.saldoCentimos)}
            </div>
            <div className="fila" style={{ marginTop: 12 }}>
              {puede("recargarCartera") && (
                <button className="boton" onClick={() => setDialogo("recarga")}>
                  {t("sf.recargar")}
                </button>
              )}
              {puede("cobrarCuota") && (
                <button className="boton secundario" onClick={() => setDialogo("cuota")}>
                  {t("sf.cobrarCuota")}
                </button>
              )}
            </div>
            {puede("ajustarCartera") && (
              <div className="fila" style={{ marginTop: 8 }}>
                {socio.saldoCentimos > 0 && (
                  <button className="boton fantasma" onClick={() => setDialogo("devolver")}>
                    {t("sf.devolverSaldo")}
                  </button>
                )}
                <button className="boton fantasma" onClick={() => setDialogo("ajustar")}>
                  {t("sf.ajustarSaldo")}
                </button>
              </div>
            )}
          </Panel>

          <div className="columna" style={{ gap: 8 }}>
            {puede("suspenderSocio") && socio.estado !== "BAJA" && socio.estado !== "SUSPENDIDO" && (
              <button className="boton secundario" onClick={() => setDialogo("suspender")}>
                {t("sf.suspender")}
              </button>
            )}
            {puede("suspenderSocio") && socio.estado === "SUSPENDIDO" && (
              <button
                className="boton"
                onClick={() =>
                  api(`/socios/${id}/levantar-suspension`, { metodo: "POST" })
                    .then(refrescar)
                    .catch((fallo) => setError(fallo instanceof Error ? fallo.message : t("sf.falloLevantar")))
                }
              >
                {t("sf.levantar")}
              </button>
            )}
            {puede("bajaSocio") && socio.estado === "BAJA" && (
              <button
                className="boton"
                onClick={() =>
                  api(`/socios/${id}/reactivar`, { metodo: "POST" })
                    .then(refrescar)
                    .catch((fallo) => setError(fallo instanceof Error ? fallo.message : t("sf.falloReactivar")))
                }
              >
                {t("sf.reactivar")}
              </button>
            )}
            {puede("editarSocio") && socio.estado !== "BAJA" && (
              <button
                className="boton secundario"
                onClick={() =>
                  api<{ codigo: string; numeroSocio: number }>(`/socios/${id}/portal`, { metodo: "POST" })
                    .then((r) => {
                      setCodigoPortal(r);
                      refrescar();
                    })
                    .catch((fallo) => setError(fallo instanceof Error ? fallo.message : t("sf.falloCodigo")))
                }
              >
                {socio.portalActivo ? t("sf.renovarPortal") : t("sf.darPortal")}
              </button>
            )}
            {puede("editarSocio") && socio.portalActivo && (
              <button
                className="boton fantasma"
                onClick={() =>
                  api(`/socios/${id}/portal`, { metodo: "DELETE" })
                    .then(refrescar)
                    .catch((fallo) => setError(fallo instanceof Error ? fallo.message : t("sf.falloRetirar")))
                }
              >
                {t("sf.retirarPortal")}
              </button>
            )}
            {puede("bajaSocio") && socio.estado !== "BAJA" && (
              <button className="boton secundario" onClick={() => setDialogo("baja")}>
                {t("sf.darDeBaja")}
              </button>
            )}

            {/* Los dos derechos que el socio puede ejercer y el club tiene que
                poder atender: pedir lo que se sabe de el, y pedir el borrado. */}
            {puede("verSocios") && (
              <button
                className="boton secundario"
                onClick={() =>
                  descargar(`/socios/${socio.id}/datos.json`, `socio-${socio.numeroSocio}-datos.json`).catch((fallo) =>
                    setError(fallo instanceof Error ? fallo.message : t("sf.falloDescargar")),
                  )
                }
              >
                {t("sf.descargarDatos")}
              </button>
            )}

            {puede("borrarDatos") && socio.nombre !== "Socio" && (
              <button className="boton peligro" onClick={() => setDialogo("borrar")}>
                {t("sf.borrarDatos")}
              </button>
            )}
          </div>
        </div>

        {/* --- Columna derecha: consumo e historial --- */}
        <div className="columna" style={{ gap: 16 }}>
          <Panel titulo={t("sf.consumoYLimites")}>
            <div className="columna" style={{ gap: 14 }}>
              <BarraLimite
                consumidoMg={consumo.consumidoHoyMg}
                limiteMg={consumo.limiteDiarioMg}
                etiqueta={t("sf.hoy")}
              />
              <BarraLimite
                consumidoMg={consumo.consumidoMesMg}
                limiteMg={consumo.limiteMensualMg}
                etiqueta={t("sf.esteMes")}
              />
            </div>
            <div className="fila separada" style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--borde)" }}>
              <span className="suave">{t("sf.puedeRetirar")}</span>
              <strong className="tabular">{gramos(consumo.disponibleMg)}</strong>
            </div>
            {socio.consumoPrevistoMensualMg > 0 && (
              <div className="fila separada pequeno suave" style={{ marginTop: 6 }}>
                <span>{t("sf.consumoDeclarado")}</span>
                <span className="tabular">
                  {t("sf.alMes", { cantidad: gramos(socio.consumoPrevistoMensualMg, 0) })}
                </span>
              </div>
            )}
          </Panel>

          <Panel sinCuerpo>
            <div className="panel-cabecera" style={{ gap: 6, flexWrap: "wrap" }}>
              {(
                [
                  ["resumen", t("sf.tab.resumen")],
                  ["dispensaciones", t("sf.tab.dispensaciones")],
                  ["cartera", t("sf.tab.cartera")],
                  ["cuotas", t("sf.tab.cuotas")],
                ] as [Pestana, string][]
              ).map(([clave, texto]) => (
                <button
                  key={clave}
                  className={`boton ${pestana === clave ? "" : "secundario"}`}
                  onClick={() => setPestana(clave)}
                >
                  {texto}
                </button>
              ))}
            </div>

            {historial.isLoading || !historial.data ? (
              <Cargando />
            ) : pestana === "resumen" ? (
              <div className="panel-cuerpo">
                <div className="rejilla tres">
                  <div>
                    <div className="rotulo">{t("sf.entregasRegistradas")}</div>
                    <div className="valor">{historial.data.dispensaciones.length}</div>
                  </div>
                  <div>
                    <div className="rotulo">{t("sf.ultimaVisita")}</div>
                    <div className="valor" style={{ fontSize: "1.1rem" }}>
                      {historial.data.accesos[0] ? fechaHora(historial.data.accesos[0].fecha) : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="rotulo">{t("sf.cuotasPagadas")}</div>
                    <div className="valor">{historial.data.cuotas.length}</div>
                  </div>
                </div>
                {socio.notas && (
                  <>
                    <h3 style={{ margin: "18px 0 6px" }}>{t("sf.notas")}</h3>
                    <p className="suave" style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                      {socio.notas}
                    </p>
                  </>
                )}
                {socio.avalados.length > 0 && (
                  <>
                    <h3 style={{ margin: "18px 0 6px" }}>{t("sf.avalados")}</h3>
                    <div className="fila ajustable">
                      {socio.avalados.map((a) => (
                        <Link key={a.id} className="etiqueta azul" to={`/socios/${a.id}`}>
                          #{a.numeroSocio} {a.nombre} {a.apellidos}
                        </Link>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : pestana === "dispensaciones" ? (
              historial.data.dispensaciones.length === 0 ? (
                <Vacio>{t("sf.sinDispensaciones")}</Vacio>
              ) : (
                <div className="tabla-envoltorio">
                  <table className="tabla">
                    <thead>
                      <tr>
                        <th>{t("sf.fecha")}</th>
                        <th>{t("sf.productos")}</th>
                        <th className="num">{t("sf.cantidad")}</th>
                        <th className="num">{t("sf.importe")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historial.data.dispensaciones.map((d) => (
                        <tr key={d.id}>
                          <td className="pequeno">
                            {fechaHora(d.fecha)}
                            {d.estado === "ANULADA" && (
                              <span className="etiqueta roja" style={{ marginLeft: 6 }}>
                                {t("sf.anulada")}
                              </span>
                            )}
                          </td>
                          <td className="pequeno">
                            {d.lineas.map((l) => `${l.nombreProducto} (${formatearCantidad(l.cantidadMg, l.unidad)})`).join(", ")}
                          </td>
                          <td className="num tabular">{gramos(d.totalMg)}</td>
                          <td className="num tabular">
                            {creditos(d.totalCentimos)}
                            <div className="suave pequeno">{t(`pago.${d.metodoPago}` as Clave)}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : pestana === "cartera" ? (
              historial.data.cartera.length === 0 ? (
                <Vacio>{t("sf.sinCartera")}</Vacio>
              ) : (
                <div className="tabla-envoltorio">
                  <table className="tabla">
                    <thead>
                      <tr>
                        <th>{t("sf.fecha")}</th>
                        <th>{t("sf.concepto")}</th>
                        <th className="num">{t("sf.importe")}</th>
                        <th className="num">{t("sf.saldo")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historial.data.cartera.map((m) => (
                        <tr key={m.id}>
                          <td className="pequeno">{fechaHora(m.fecha)}</td>
                          <td>
                            <span className="etiqueta">{t(`movimiento.${m.tipo}` as Clave)}</span>{" "}
                            <span className="pequeno suave">{m.concepto}</span>
                          </td>
                          <td
                            className="num tabular"
                            style={{ color: m.importeCentimos >= 0 ? "var(--verde)" : "var(--rojo)" }}
                          >
                            {m.importeCentimos >= 0 ? "+" : ""}
                            {creditos(m.importeCentimos)}
                          </td>
                          <td className="num tabular">{creditos(m.saldoResultante)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : historial.data.cuotas.length === 0 ? (
              <Vacio>{t("sf.sinCuotas")}</Vacio>
            ) : (
              <div className="tabla-envoltorio">
                <table className="tabla">
                  <thead>
                    <tr>
                      <th>{t("sf.fecha")}</th>
                      <th>{t("sf.concepto")}</th>
                      <th>{t("sf.validaHasta")}</th>
                      <th className="num">{t("sf.importe")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historial.data.cuotas.map((c) => (
                      <tr key={c.id}>
                        <td className="pequeno">{fecha(c.fecha)}</td>
                        <td>
                          {c.concepto}
                          <div className="suave pequeno">{t(`pago.${c.metodoPago}` as Clave)}</div>
                        </td>
                        <td className="pequeno">{fecha(c.periodoFin)}</td>
                        <td className="num tabular">{creditos(c.importeCentimos)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>
      </div>

      {codigoPortal && (
        <Modal
          titulo={t("sf.codigoPortal")}
          alCerrar={() => setCodigoPortal(null)}
          acciones={
            <button className="boton" onClick={() => setCodigoPortal(null)}>
              {t("sf.hecho")}
            </button>
          }
        >
          <p style={{ marginTop: 0 }}>{t("sf.daleLosDatos")}</p>
          <div className="centrado" style={{ margin: "18px 0" }}>
            <div className="rotulo">{t("sf.numeroDeSocio")}</div>
            <div className="valor tabular">{codigoPortal.numeroSocio}</div>
            <div className="rotulo" style={{ marginTop: 12 }}>
              {t("sf.codigo")}
            </div>
            <div className="valor tabular" style={{ letterSpacing: "0.12em" }}>{codigoPortal.codigo}</div>
          </div>
          <p className="suave pequeno" style={{ marginBottom: 0 }}>
            {t("sf.entraEn", { url: `${window.location.origin}/portal/${club?.slug ?? ""}` })}
          </p>
        </Modal>
      )}

      {/* El mismo diálogo que el mostrador: así el cálculo del cambio está en
          los dos sitios donde se cobra en efectivo, y no en uno solo. */}
      {dialogo === "recarga" && (
        <DialogoRecarga
          socio={socio}
          alCerrar={() => setDialogo(null)}
          alHecho={() => {
            setDialogo(null);
            refrescar();
          }}
          alFallar={(mensaje) => {
            setDialogo(null);
            setError(mensaje);
          }}
        />
      )}

      {dialogo === "cuota" && (
        <DialogoCobro
          socio={socio}
          importePorDefecto={(socio.tipoSocio?.cuotaAnualCentimos ?? club?.cuotaAnualCentimos ?? 2000) / 100}
          alCerrar={() => setDialogo(null)}
          alHecho={() => {
            setDialogo(null);
            refrescar();
          }}
          alFallar={(mensaje) => {
            setDialogo(null);
            setError(mensaje);
          }}
        />
      )}

      {dialogo === "devolver" && (
        <DialogoImporte
          titulo={t("sf.devolverTitulo")}
          etiqueta={t("sf.devolverEtiqueta")}
          valorInicial={socio.saldoCentimos / 100}
          maximo={socio.saldoCentimos / 100}
          conConcepto
          alConfirmar={async (importe, _metodo, concepto) => {
            await api("/cartera/devolver", {
              metodo: "POST",
              cuerpo: { socioId: id, importeEuros: importe, concepto: concepto || undefined },
            });
            refrescar();
          }}
          alCerrar={() => setDialogo(null)}
          alFallar={setError}
        />
      )}

      {dialogo === "ajustar" && (
        <DialogoImporte
          titulo={t("sf.ajustarTitulo")}
          etiqueta={t("sf.ajustarEtiqueta")}
          valorInicial={0}
          permitirNegativo
          conConcepto
          conceptoObligatorio
          alConfirmar={async (importe, _metodo, concepto) => {
            await api("/cartera/ajustar", {
              metodo: "POST",
              cuerpo: { socioId: id, importeEuros: importe, concepto },
            });
            refrescar();
          }}
          alCerrar={() => setDialogo(null)}
          alFallar={setError}
        />
      )}

      {dialogo === "baja" && (
        <DialogoMotivo
          titulo={t("sf.bajaTitulo")}
          texto={t("sf.bajaTexto")}
          boton={t("sf.darDeBaja")}
          alConfirmar={async (motivo) => {
            await api(`/socios/${id}/baja`, { metodo: "POST", cuerpo: { motivo } });
            navegar("/socios");
          }}
          alCerrar={() => setDialogo(null)}
          alFallar={setError}
        />
      )}

      {dialogo === "borrar" && (
        <DialogoBorrarDatos
          numeroSocio={socio.numeroSocio}
          alConfirmar={async (confirmacion) => {
            await api(`/socios/${id}/borrar-datos`, { metodo: "POST", cuerpo: { confirmacion } });
            navegar("/socios");
          }}
          alCerrar={() => setDialogo(null)}
          alFallar={setError}
        />
      )}

      {dialogo === "suspender" && (
        <DialogoMotivo
          titulo={t("sf.suspenderTitulo")}
          texto={t("sf.suspenderTexto")}
          boton={t("sf.suspenderBoton")}
          alConfirmar={async (motivo) => {
            await api(`/socios/${id}/suspender`, { metodo: "POST", cuerpo: { motivo } });
            refrescar();
          }}
          alCerrar={() => setDialogo(null)}
          alFallar={setError}
        />
      )}
    </>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: React.ReactNode }) {
  return (
    <div className="fila separada pequeno">
      <span className="suave">{etiqueta}</span>
      <span style={{ textAlign: "right" }}>{valor}</span>
    </div>
  );
}

function DialogoImporte({
  titulo,
  etiqueta,
  valorInicial = 20,
  maximo,
  conMetodoPago,
  conCartera,
  conConcepto,
  conceptoObligatorio,
  permitirNegativo,
  alConfirmar,
  alCerrar,
  alFallar,
}: {
  titulo: string;
  etiqueta: string;
  valorInicial?: number;
  maximo?: number;
  conMetodoPago?: boolean;
  conCartera?: boolean;
  conConcepto?: boolean;
  conceptoObligatorio?: boolean;
  permitirNegativo?: boolean;
  alConfirmar: (importe: number, metodoPago: string, concepto: string) => Promise<void>;
  alCerrar: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  const [importe, setImporte] = useState(String(valorInicial));
  const [metodoPago, setMetodoPago] = useState("EFECTIVO");
  const [concepto, setConcepto] = useState("");
  const [enviando, setEnviando] = useState(false);

  const valor = Number(importe);
  const importeValido = Number.isFinite(valor) && (permitirNegativo ? valor !== 0 : valor > 0);
  const dentroDelMaximo = maximo === undefined || valor <= maximo;
  const conceptoValido = !conceptoObligatorio || concepto.trim().length >= 3;

  async function confirmar() {
    setEnviando(true);
    try {
      await alConfirmar(valor, metodoPago, concepto.trim());
      alFallar("");
      alCerrar();
    } catch (fallo) {
      alFallar(fallo instanceof Error ? fallo.message : t("general.fallo"));
      alCerrar();
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo={titulo}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            {t("g.cancelar")}
          </button>
          <button
            className="boton"
            onClick={confirmar}
            disabled={enviando || !importeValido || !dentroDelMaximo || !conceptoValido}
          >
            {enviando ? t("g.guardando") : t("sf.confirmar")}
          </button>
        </>
      }
    >
      <Campo
        etiqueta={etiqueta}
        ayuda={maximo !== undefined ? t("sf.comoMaximo", { importe: maximo.toFixed(2) }) : undefined}
      >
        <input
          type="number"
          min={permitirNegativo ? undefined : 0}
          max={maximo}
          step="0.01"
          value={importe}
          autoFocus
          onChange={(e) => setImporte(e.target.value)}
        />
      </Campo>
      {conConcepto && (
        <Campo etiqueta={conceptoObligatorio ? t("sf.motivoObligatorio") : t("sf.concepto")}>
          <input value={concepto} onChange={(e) => setConcepto(e.target.value)} />
        </Campo>
      )}
      {conMetodoPago && (
        <Campo etiqueta={t("sf.formaDePago")}>
          <select value={metodoPago} onChange={(e) => setMetodoPago(e.target.value)}>
            {["EFECTIVO", "TARJETA", "TRANSFERENCIA"].map((m) => (
              <option key={m} value={m}>
                {t(`pago.${m}` as Clave)}
              </option>
            ))}
            {conCartera && <option value="CARTERA">{t("dc.carteraDelSocio")}</option>}
          </select>
        </Campo>
      )}
    </Modal>
  );
}

function DialogoMotivo({
  titulo,
  texto,
  boton,
  alConfirmar,
  alCerrar,
  alFallar,
}: {
  titulo: string;
  texto: string;
  boton: string;
  alConfirmar: (motivo: string) => Promise<void>;
  alCerrar: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal
      titulo={titulo}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            {t("g.cancelar")}
          </button>
          <button
            className="boton peligro"
            disabled={enviando || motivo.trim().length < 3}
            onClick={async () => {
              setEnviando(true);
              try {
                await alConfirmar(motivo);
                alCerrar();
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : t("sf.falloAccion"));
                alCerrar();
              } finally {
                setEnviando(false);
              }
            }}
          >
            {boton}
          </button>
        </>
      }
    >
      <p className="suave" style={{ marginTop: 0 }}>
        {texto}
      </p>
      <Campo etiqueta={t("sf.motivo")}>
        <textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} autoFocus />
      </Campo>
    </Modal>
  );
}

/**
 * Borrado de los datos personales de un socio.
 *
 * Explica exactamente qué se va y qué se queda, porque las dos mitades
 * importan: la gente espera que "borrar" borre todo, y aquí no puede ser —las
 * dispensaciones y los cobros son contabilidad del club—. Y avisa de las copias
 * de seguridad, que es lo que todo el mundo se olvida de contar.
 */
function DialogoBorrarDatos({
  numeroSocio,
  alConfirmar,
  alCerrar,
  alFallar,
}: {
  numeroSocio: number;
  alConfirmar: (confirmacion: string) => Promise<void>;
  alCerrar: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  const [confirmacion, setConfirmacion] = useState("");
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal
      titulo={t("sf.borrarTitulo")}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            {t("g.cancelar")}
          </button>
          <button
            className="boton peligro"
            disabled={enviando || confirmacion.trim() !== String(numeroSocio)}
            onClick={async () => {
              setEnviando(true);
              try {
                await alConfirmar(confirmacion);
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : t("sf.falloBorrar"));
                setEnviando(false);
              }
            }}
          >
            {enviando ? t("sf.borrando") : t("sf.borrarDatos")}
          </button>
        </>
      }
    >
      <Aviso tipo="error">{t("sf.sinVuelta")}</Aviso>

      <p style={{ marginTop: 0 }}>
        <strong>{t("sf.seBorra")}</strong> {t("sf.seBorraLista")}
      </p>
      <p>
        <strong>{t("sf.seConserva")}</strong> {t("sf.seConservaLista")}
      </p>
      <p className="suave pequeno">{t("sf.copiasDeSeguridad")}</p>

      <Campo etiqueta={t("sf.escribeNumero", { numero: numeroSocio })}>
        <input value={confirmacion} onChange={(e) => setConfirmacion(e.target.value)} autoFocus />
      </Campo>
    </Modal>
  );
}

/**
 * Las dos caras del documento, tal como se fotografiaron al dar el alta.
 *
 * Son la unica forma de comprobar despues que quien entro era quien decia ser,
 * asi que tienen que poder mirarse; subirlas y no poder verlas nunca no sirve
 * de nada. Si no hay ninguna el bloque no ocupa sitio: hay clubes que no las
 * guardan.
 */
function FotosDelDocumento({
  socio,
  alCambiar,
}: {
  socio: { id: string; docFrenteUrl: string | null; docReversoUrl: string | null };
  alCambiar: () => void;
}) {
  const { t } = useIdioma();
  const { puede } = useSesion();
  const [subiendo, setSubiendo] = useState<string | null>(null);
  const caras = [
    { cual: "documento-frente", titulo: t("sf.frente"), url: socio.docFrenteUrl },
    { cual: "documento-reverso", titulo: t("sf.reverso"), url: socio.docReversoUrl },
  ];

  if (!socio.docFrenteUrl && !socio.docReversoUrl && !puede("editarSocio")) return null;

  return (
    <Panel titulo={t("sf.documento")}>
      <div className="capturas">
        {caras.map((cara) => (
          <div key={cara.cual} className="captura">
            <div className="captura-marco">
              {cara.url ? (
                <a href={cara.url} target="_blank" rel="noreferrer">
                  <img src={cara.url} alt={cara.titulo} />
                </a>
              ) : (
                <div className="captura-vacia">
                  <span className="suave pequeno">{t("sf.sinFoto")}</span>
                </div>
              )}
            </div>
            <strong className="pequeno">{cara.titulo}</strong>
            {puede("editarSocio") && (
              <label className="boton fantasma" style={{ cursor: "pointer" }}>
                {subiendo === cara.cual ? t("sf.subiendo") : cara.url ? t("sf.cambiar") : t("sf.subir")}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  hidden
                  onChange={async (e) => {
                    const archivo = e.target.files?.[0];
                    e.target.value = "";
                    if (!archivo) return;
                    setSubiendo(cara.cual);
                    try {
                      await subirArchivo(`/socios/${socio.id}/imagen/${cara.cual}`, archivo);
                      alCambiar();
                    } finally {
                      setSubiendo(null);
                    }
                  }}
                />
              </label>
            )}
          </div>
        ))}
      </div>
    </Panel>
  );
}
